
import Page from '@/Page.vue';
import { Component, Prop, VModel, Watch } from 'vue-property-decorator';
import { shiftScrapRecordsStore, shiftsStore, shiftStationsStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudReponse, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import {
    GroupModel,
    LocationModel,
    ProcessTypeEnum,
    ProductModel,
    QueryOperator,
    ShiftModel,
    ShiftProductionModel,
    ShiftScrapRecordModel,
    ShiftStateEnum,
    ShiftStationModel,
    ShiftTypeModel,
    StationModel,
    TenantUserModel,
} from '@/libs/Api';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import moment from 'moment/moment';
import IssueSolvingItemSmall from '@/libs/issues-solving/components/IssueSolvingItemSmall.vue';
import IssueSolvingItem from '@/libs/issues-solving/components/IssueSolvingItem.vue';
import ProductSelect from '@/libs/common/components-smart/ProductSelect.vue';
import { Guid } from '@/libs/common/functions/guid';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { cloneDeep } from 'lodash';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { locationsStore } from '@/libs/locations/+state/store';
import { groupsStore } from '@/libs/groups/+state/store';
import { stationsStore } from '@/libs/stations/+state/store';
import ShiftScrapRecordDialog from '@/libs/shifts/components/ShiftScrapRecordDialog.vue';

@Component({
    components: { ShiftScrapRecordDialog, ShiftTypeSelect, ProductSelect, IssueSolvingItem, IssueSolvingItemSmall },
})
export default class EditShiftDetailDialog extends Page {
    @VModel() dialog!: boolean;
    @Prop() id!: string;
    @Prop() tab!: number;
    @Prop() initialData!: ShiftModel | null;
    @Prop() initialDate!: string | null;
    @Prop() name!: string | null;

    protected fromDate: string | null = null;
    protected toDate: string | null = null;

    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected ShiftStateEnum: typeof ShiftStateEnum = ShiftStateEnum;
    protected tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    protected tabValue = 1;
    protected shift: ShiftModel | null = null;
    protected filterProductId: string | null = null;
    protected deleteDialog = false;
    protected saveCollisionDialog = false;

    protected shiftProductionGroups: {
        time: string;
        isActive: boolean;
        items: { shiftProduction: ShiftProductionModel; okCount: number | null; nokCount: number | null }[];
    }[] = [];

    private isSavingFullShift = false;

    @Watch('shift')
    shiftChanged(v: ShiftModel | null) {
        if (!v) {
            this.dialog = false;
        }
    }

    get isReadonly(): boolean {
        return this.shift?.shiftState == ShiftStateEnum.Closed;
    }

    get isDeleting(): boolean {
        return shiftsStore.useGetter(CrudGetter.Deleting) ?? false;
    }

    get canDelete(): boolean {
        return this.id != Guid.EmptyGuid() && this.id != null;
    }

    get isNew(): boolean {
        return this.id == Guid.EmptyGuid() || this.id == null;
    }

    get isSaveDisabled(): boolean {
        return (
            !this.shift ||
            !this.fromDate ||
            !this.toDate ||
            new Date(this.fromDate!).getTime() >= new Date(this.toDate!).getTime()
        );
    }

    get canUpdateRecords(): boolean {
        return this.shift != null && true && this.fromDate != undefined && true && this.toDate != null && true;
    }

    get stations(): StationModel[] {
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'EdiShiftDetailDialog') ?? [];
        let items: StationModel[] = stationsStore.useGetter(CrudGetter.Data) ?? [];
        items = items.filter((x) => x.workplaceId == this.shift?.workplaceId);
        items = [
            ...items,
            ...shiftStations.filter((x) => !items.some((n) => n.id == x.stationId) && x.station).map((e) => e.station!),
        ];
        return items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    async mounted() {
        this.$store.subscribe((mutation) => {
            if (mutation.type === shiftsStore.getActionName(CrudResponseAction.UpdateSuccess)) {
                if (mutation.payload.item.id == this.id) {
                    this.fromDate = mutation.payload.item?.fromDate ?? null;
                    this.toDate = mutation.payload.item?.toDate ?? null;
                    this.updateShiftProductions();
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.PartialUpdateSuccess)) {
                if (mutation.payload.item.id == this.id) {
                    this.fromDate = mutation.payload.item?.fromDate ?? null;
                    this.toDate = mutation.payload.item?.toDate ?? null;
                    this.updateShiftProductions();
                }
            }
        });
        this.tabValue = this.tab ?? 1;
        await this.loadShift();
        await this.loadStationOccupation();
    }

    async loadShift() {
        this.$nextTick(async () => {
            if (this.id != null && this.id != Guid.EmptyGuid()) {
                shiftsStore.dispatch(CrudAction.Get, {
                    id: this.id,
                    key: 'ShiftDetailDialog',
                });
                this.shift = await this.subscribe(shiftsStore, CrudReponse.Get, 'ShiftDetailDialog');
            } else {
                this.shift = {
                    id: Guid.EmptyGuid(),
                    shiftProductions: [],
                };
                if (this.initialData) {
                    this.shift = {
                        ...cloneDeep(this.initialData),
                        ...this.shift,
                    };
                }
            }
            this.fromDate = this.shift?.fromDate ?? null;
            this.toDate = this.shift?.toDate ?? null;
            this.updateShiftProductions();
        });
    }

    async loadStationOccupation() {
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                undefined,
                [{ field: 'shiftId', op: 'eq', comparand: this.id }],
                undefined,
                false,
                'EdiShiftDetailDialog'
            )
        );
        await this.subscribe(shiftStationsStore, CrudReponse.GetAll, 'EdiShiftDetailDialog');
    }

    updateShiftProductions() {
        if (this.isSavingFullShift) {
            return;
        }
        const shift = this.shift!;
        if (!shift) {
            return;
        }
        const shiftProductionGroups: {
            fromTime: string | null;
            time: string;
            isActive: boolean;
            items: { shiftProduction: ShiftProductionModel; okCount: number | null; nokCount: number | null }[];
        }[] = [];
        const fromDate = moment(this.fromDate!).add(60, 'minutes').toDate();
        let toDate = new Date(shift.toDate!);
        if (toDate.getMinutes() > 0) {
            toDate = moment(toDate).startOf('hour').add(1, 'hour').toDate();
        }
        let time = fromDate;
        while (time <= toDate) {
            const groupTimeMoment = moment(time).isAfter(shift.toDate) ? moment(shift.toDate) : moment(time);
            const isLastHourGroup = groupTimeMoment.isSame(moment(shift.toDate));
            const thresholdMinutesForGroup = isLastHourGroup ? 30 : 10;
            shiftProductionGroups.push({
                fromTime: moment(time).subtract(60, 'minutes').format('YYYY-MM-DDTHH:mm:ss'),
                time: groupTimeMoment.format('YYYY-MM-DDTHH:mm:ss'),
                isActive: groupTimeMoment.isBefore(moment().add(thresholdMinutesForGroup, 'minutes')),
                items: [],
            });
            time = new Date(time.getTime() + 60 * 60 * 1000);
        }
        for (const shiftProduction of shift.shiftProductions ?? []) {
            for (const shiftProductionRecord of shiftProduction.shiftProductionRecords ?? []) {
                if (!shiftProductionGroups.some((x) => moment(x.time).isSame(shiftProductionRecord.recordTime))) {
                    const recordTimeMoment = moment(shiftProductionRecord.recordTime!);
                    const isRecordLastHour = recordTimeMoment.isSame(moment(shift.toDate));
                    const recordThresholdMinutes = isRecordLastHour ? 30 : 10;
                    shiftProductionGroups.push({
                        fromTime: null,
                        time: shiftProductionRecord.recordTime!,
                        isActive: recordTimeMoment.isBefore(moment().add(recordThresholdMinutes, 'minutes')),
                        items: [],
                    });
                }
            }
        }
        for (const shiftProductionGroup of shiftProductionGroups) {
            time = new Date(shiftProductionGroup.time);
            shiftProductionGroup.items = (shift.shiftProductions ?? []).map((p) => {
                const shiftProductionRecord = (p.shiftProductionRecords ?? [])
                    .filter((r) => new Date(r.recordTime!).getTime() == time.getTime())
                    .shift();
                return {
                    shiftProduction: p,
                    okCount: shiftProductionRecord?.okCount ?? null,
                    nokCount: shiftProductionRecord?.nokCount ?? null,
                };
            });
        }
        this.shiftProductionGroups = shiftProductionGroups;
    }

    transferShiftProductions() {
        const item = this.shift!;
        (item.shiftProductions ?? []).forEach((p) => {
            const savedRecords = p.shiftProductionRecords ?? [];
            for (const record of savedRecords) {
                const shiftProductionRecord = this.shiftProductionGroups
                    .filter((x) => moment(x.time).isSame(record.recordTime))
                    .shift()
                    ?.items.filter((x) => x.shiftProduction.id == p.id)
                    .shift();
                if (shiftProductionRecord != null && (shiftProductionRecord.okCount || shiftProductionRecord.nokCount)) {
                    record.okCount =
                        shiftProductionRecord.okCount && Number(shiftProductionRecord.okCount) >= 0
                            ? Number(shiftProductionRecord.okCount)
                            : null;
                    record.nokCount =
                        shiftProductionRecord.nokCount && Number(shiftProductionRecord.okCount) >= 0
                            ? Number(shiftProductionRecord.nokCount)
                            : null;
                } else {
                    savedRecords.splice(savedRecords.indexOf(record), 1);
                }
            }
            this.shiftProductionGroups.forEach((x) => {
                let shiftProductionRecord = x.items.filter((x) => x.shiftProduction.id == p.id).shift();
                if (!shiftProductionRecord) {
                    shiftProductionRecord = x.items.filter((x) => x.shiftProduction.productId == p.productId).shift();
                }
                if (shiftProductionRecord) {
                    const okCount =
                        shiftProductionRecord.okCount && Number(shiftProductionRecord.okCount) >= 0
                            ? Number(shiftProductionRecord.okCount)
                            : null;
                    const nokCount =
                        shiftProductionRecord.nokCount && Number(shiftProductionRecord.okCount) >= 0
                            ? Number(shiftProductionRecord.nokCount)
                            : null;
                    if (
                        savedRecords.filter((r) => new Date(r.recordTime!).getTime() == new Date(x.time).getTime()).length ==
                            0 &&
                        (okCount != null || nokCount != null)
                    ) {
                        savedRecords.push({
                            recordTime: x.time,
                            okCount,
                            nokCount,
                        });
                    }
                }
            });
            p.okCount = savedRecords.filter((r) => r.okCount != null).reduce((a, b) => a + b.okCount!, 0);
            p.nokCount = savedRecords.filter((r) => r.nokCount != null).reduce((a, b) => a + b.nokCount!, 0);
        });
        item.okCount = (item.shiftProductions ?? []).reduce((a, b) => a + b.okCount!, 0);
        item.nokCount = (item.shiftProductions ?? []).reduce((a, b) => a + b.nokCount!, 0);
    }

    async onSave(skipCollisions = false) {
        this.isSavingFullShift = true;
        const item = this.shift!;
        item.fromDate = this.fromDate!;
        item.toDate = this.toDate!;
        shiftsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: 'asc' },
                [
                    { field: 'workplaceId', op: QueryOperator.Eq, comparand: item.workplaceId },
                    { field: 'fromDate', op: QueryOperator.Lt, comparand: item.toDate },
                    { field: 'toDate', op: QueryOperator.Gt, comparand: item.fromDate },
                    { field: 'id', op: QueryOperator.Ne, comparand: item.id },
                ],
                { pageIndex: 1, pageSize: 1 },
                false,
                'ShiftsInCollision'
            )
        );
        if (!skipCollisions) {
            const shiftsInCollision = await this.subscribe(shiftsStore, CrudReponse.GetAll, 'ShiftsInCollision');
            if (shiftsInCollision.length > 0) {
                this.saveCollisionDialog = true;
                this.isSavingFullShift = false;
                return;
            }
        }
        if (this.shift!.id == Guid.EmptyGuid()) {
            shiftsStore.dispatch(CrudAction.Create, { item });
            const newShift = await this.subscribe(shiftsStore, CrudReponse.Create);
            this.$emit('created', newShift);
        } else {
            shiftsStore.dispatch(CrudAction.Update, { item });
            await this.subscribe(shiftsStore, CrudReponse.Update);
        }
        this.isSavingFullShift = false;
        this.onCloseClick();
    }

    protected getTotalOkCount(productId: string): number | null {
        return (this.shiftProductionGroups ?? []).reduce(
            (a, b) =>
                a +
                (b.items ?? [])
                    .filter((x) => x.shiftProduction?.productId == productId)
                    .reduce((a, b) => a + ((!isNaN(Number(b.okCount)) ? Number(b.okCount) : 0) ?? 0), 0),
            0
        );
    }

    protected getTotalNokCount(shiftProductionId: string): number | null {
        return (this.shiftProductionGroups ?? []).reduce(
            (a, b) =>
                a +
                (b.items ?? [])
                    .filter((x) => x.shiftProduction?.productId == shiftProductionId)
                    .reduce((a, b) => a + ((!isNaN(Number(b.nokCount)) ? Number(b.nokCount) : 0) ?? 0), 0),
            0
        );
    }

    protected onCloseClick() {
        this.$emit('input', false);
        this.$emit('close');
    }

    protected onProductAdded(product: ProductModel) {
        this.filterProductId = null;
        if (product) {
            if (!this.shift!.shiftProductions!.some((x) => x.productId == product!.id)) {
                this.shift!.shiftProductions!.push({
                    id: Guid.EmptyGuid(),
                    productId: product.id,
                    product: product,
                    planCount: null,
                    okCount: null,
                    nokCount: null,
                    shiftProductionRecords: [],
                });
            }
            this.updateShiftProductions();
        }
    }

    onShiftTypeUpdated(shiftType: ShiftTypeModel) {
        if (this.shift && (this.initialDate || this.fromDate)) {
            const initialDate = this.initialDate ?? moment(this.fromDate).format('YYYY-MM-DD');
            const fromDate = moment(`${initialDate}T${shiftType.fromTime}`);
            let toDate = moment(`${initialDate}T${shiftType.toTime}`);
            if (toDate.isBefore(fromDate)) {
                toDate = toDate.add(1, 'day');
            }
            this.fromDate = fromDate.format('YYYY-MM-DDTHH:mm:ss');
            this.toDate = toDate.format('YYYY-MM-DDTHH:mm:ss');
            this.$nextTick(() => {
                this.updateShiftProductions();
            });
        }
    }

    onDeleteShiftClick() {
        this.deleteDialog = true;
    }

    async onDeleteShift() {
        shiftsStore.dispatch(CrudAction.Delete, { id: this.id });
        await this.subscribe(shiftsStore, CrudReponse.Delete);
        this.$emit('deleted');
        this.onCloseClick();
    }

    getAvailableOfficers(): TenantUserModel[] {
        const shift: ShiftModel | null = this.shift ?? null;
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const groups: GroupModel[] = groupsStore.useGetter(CrudGetter.Data) ?? [];
        if (!shift) {
            return [];
        }
        let locationId = shift.workplace!.locationId;
        let location = locations.find((x) => x.id == locationId);
        let trials = [locationId];
        while (location) {
            if (location.canPlanShifts) {
                break;
            } else {
                location = locations.find((x) => x.id == location?.parentalLocationId);
                if (trials.contains(location?.id)) {
                    break;
                }
            }
        }
        let items: TenantUserModel[] = [];
        if (location && location.canPlanShifts && location.locationGroups!.length > 0) {
            const groupIds = location.locationGroups!.map((x) => x.groupId);
            items = groups
                .filter((x) => groupIds.contains(x.id!))!
                .selectMany((x) => x.groupUsers!.select((u) => u.tenantUser!).toArray())
                .toArray()
                .reduce((acc, x) => {
                    if (!acc.find((a: any) => a.id == x.id)) {
                        acc.push(x);
                    }
                    return acc;
                }, [] as TenantUserModel[])
                .toArray();
        }
        if (shift.officer != null && !items.find((x) => x.id == shift.officerId)) {
            items.push(shift.officer);
        }
        return items;
    }

    getStationWorker(stationId: string): string | null {
        const shiftStations: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'EdiShiftDetailDialog') ?? [];
        return shiftStations.find((x) => x.stationId == stationId)?.workerId ?? null;
    }

    async setStationWorker(stationId: string, workerId: string | null) {
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<ShiftStationModel>(
                undefined,
                [
                    { field: 'shiftId', op: 'eq', comparand: this.id },
                    { field: 'stationId', op: 'eq', comparand: stationId },
                ],
                undefined,
                false,
                'SetStationWorker'
            )
        );
        const shiftStations: ShiftStationModel[] = await this.subscribe(
            shiftStationsStore,
            CrudReponse.GetAll,
            'SetStationWorker'
        );
        if (workerId && shiftStations.length == 0) {
            shiftStationsStore.dispatch(CrudAction.Create, {
                item: {
                    shiftId: this.id,
                    stationId,
                    workerId,
                } as ShiftStationModel,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Create, 'SetStationWorker');
            await this.loadStationOccupation();
        } else if (workerId && shiftStations.length > 0) {
            const shiftStation = shiftStations[0];
            shiftStation.workerId = workerId;
            shiftStationsStore.dispatch(CrudAction.Update, {
                item: shiftStation,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Update, 'SetStationWorker');
            await this.loadStationOccupation();
        } else if (!workerId && shiftStations.length > 0) {
            const shiftStation = shiftStations[0];
            shiftStationsStore.dispatch(CrudAction.Delete, {
                id: shiftStation.id,
                key: 'SetStationWorker',
            });
            await this.subscribe(shiftStationsStore, CrudReponse.Delete, 'SetStationWorker');
            await this.loadStationOccupation();
        }
    }

    getShiftScrapRecords(
        fromTime: string | null,
        toTime: string | null,
        productId: string,
        isLast: boolean
    ): ShiftScrapRecordModel[] {
        if (!fromTime || !toTime) {
            return [];
        }
        const fromTimeValue = new Date(fromTime);
        const toTimeValue = new Date(toTime);
        const shiftScrapRecords: ShiftScrapRecordModel[] = shiftScrapRecordsStore.useGetter(CrudGetter.Data) ?? [];
        return shiftScrapRecords.filter(
            (x) =>
                x.productId == productId &&
                new Date(x.recordTime!).getTime() >= fromTimeValue.getTime() &&
                (new Date(x.recordTime!).getTime() < toTimeValue.getTime() ||
                    (isLast && new Date(x.recordTime!).getTime() <= toTimeValue.getTime()))
        );
    }

    getShiftScrapRecordCount(fromTime: string | null, toTime: string | null, productId: string, isLast: boolean): number {
        return this.getShiftScrapRecords(fromTime, toTime, productId, isLast).reduce((acc, c) => acc + (c.scrapCount ?? 0), 0);
    }
}
